import React, { useEffect, useState } from 'react';
import { useTextHighlighting } from '@/providers/TextHighlightingProvider';
import { TextBlock } from '@/components/editor/TextBlock';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/InputDefault';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Sparkles, Plus, Trash2 } from 'lucide-react';
import { highlightDataService } from '@/lib/services/HighlightDataService';

import { usePlatformTranslation } from '@/hooks/usePlatformTranslation';
const TextHighlightingTestPage: React.FC = () => {
  const { t } = usePlatformTranslation();
  const { addHighlightItems, highlightItems, clearAllHighlights, removeHighlightItem } = useTextHighlighting();
  const [newHighlightKey, setNewHighlightKey] = useState('');
  const [newHighlightType, setNewHighlightType] = useState('glossary');
  const [testContent, setTestContent] = useState('');

  useEffect(() => {
    // Set up sample highlight items
    const sampleItems = [
      {
        keys: ['character', 'hero', 'protagonist', 'warrior', 'adventurer'],
        type: 'glossary',
        id: 'char-001'
      },
      {
        keys: ['sword', 'weapon', 'blade', 'dagger', 'axe'],
        type: 'type',
        id: 'weapon-001'
      },
      {
        keys: ['magic', 'spell', 'enchantment', 'sorcery', 'wizardry'],
        type: 'glossary',
        id: 'magic-001'
      },
      {
        keys: ['dragon', 'beast', 'monster', 'creature', 'wyrm'],
        type: 'type',
        id: 'creature-001'
      },
      {
        keys: ['quest', 'mission', 'adventure', 'journey', 'expedition'],
        type: 'glossary',
        id: 'quest-001'
      }
    ];
    
    addHighlightItems(sampleItems);
    
    // Set sample content that includes "inventory" to test highlighting
    setTestContent(`The brave hero embarked on a dangerous quest to defeat the ancient dragon. 
    
With his trusty sword and powerful magic spells, the warrior ventured into the dark forest where the beast lurked. The protagonist knew this would be his greatest adventure yet, facing monsters and creatures beyond imagination.

The adventurer's journey would test his skills and courage as he fought to protect the realm from the wyrm's sorcery and enchantments.

Don't forget to check your inventory for useful items and equipment!`);
    
    // Debug: Log the current highlight items
    console.log('TestPage - Added sample items:', sampleItems);
  }, [addHighlightItems]);

  // Debug: Log when highlight items change
  useEffect(() => {
    console.log('TestPage - Highlight items updated:', highlightItems);
  }, [highlightItems]);

  const handleContentChange = (id: string, content: string) => {
    console.log(`Content changed for ${id}:`, content);
    setTestContent(content);
  };

  const handleEnter = (id: string) => {
    console.log(`Enter pressed for ${id}`);
  };

  const handleBackspace = (id: string) => {
    console.log(`Backspace pressed for ${id}`);
  };

  const handleFocus = (id: string) => {
    console.log(`Focused on ${id}`);
  };

  const handleArrowUp = (id: string) => {
    console.log(`Arrow up for ${id}`);
  };

  const handleArrowDown = (id: string) => {
    console.log(`Arrow down for ${id}`);
  };

  const handleCommand = (id: string, command: string) => {
    console.log(`Command ${command} for ${id}`);
  };

  const addNewHighlight = () => {
    if (newHighlightKey.trim()) {
      const newItem = {
        keys: [newHighlightKey.trim()],
        type: newHighlightType,
        id: `custom-${Date.now()}`
      };
      addHighlightItems([newItem]);
      setNewHighlightKey('');
    }
  };

  const getHighlightCount = () => {
    return highlightItems.reduce((total, item) => total + item.keys.length, 0);
  };

  return (
    <div className="container mx-auto p-6 max-w-6xl space-y-6">
      <div className="text-center">
        <h1 className="text-3xl font-bold mb-2">{t('platform.pages.test_pages.text_highlighting_test_page')}</h1>
        <p className="text-gray-600">{t('platform.pages.test_pages.test_the_highlighting_functionality_in_the_textblock_component')}</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Highlight Management */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-purple-600" />
              Highlight Management
            </CardTitle>
            <CardDescription>
              Manage highlight items and see statistics
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Add new highlight */}
            <div className="space-y-2">
              <Label htmlFor="new-key">{t('platform.pages.test_pages.add_new_highlight_key')}</Label>
              <div className="flex gap-2">
                <Input
                  id="new-key"
                  placeholder={t('platform.pages.test_pages.enter_highlight_key')}
                  value={newHighlightKey}
                  onChange={(e) => setNewHighlightKey(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && addNewHighlight()}
                />
                <select
                  value={newHighlightType}
                  onChange={(e) => setNewHighlightType(e.target.value)}
                  className="px-3 py-2 border rounded-md"
                >
                  <option value="glossary">{t('platform.pages.test_pages.glossary')}</option>
                  <option value="type">{t('platform.common.type')}</option>
                  <option value="custom">{t('platform.pages.test_pages.custom')}</option>
                </select>
                <Button onClick={addNewHighlight} size="sm">
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
            </div>

            {/* Statistics */}
            <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
              <div className="text-center">
                <div className="text-2xl font-bold text-purple-600">{highlightItems.length}</div>
                <div className="text-sm text-gray-600">{t('platform.pages.test_pages.highlight_types')}</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-600">{getHighlightCount()}</div>
                <div className="text-sm text-gray-600">{t('platform.pages.test_pages.total_keys')}</div>
              </div>
            </div>

            {/* Current highlights */}
            <div className="space-y-2">
              <Label>{t('platform.pages.test_pages.current_highlight_items')}</Label>
              <div className="max-h-40 overflow-y-auto space-y-2">
                {highlightItems.map((item) => (
                  <div
                    key={item.id}
                    className="flex items-center justify-between p-2 bg-white border rounded"
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="text-xs">
                          {item.type}
                        </Badge>
                        <span className="text-sm">{item.keys.join(', ')}</span>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeHighlightItem(item.id)}
                      className="text-red-600 hover:text-red-700"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>

            {/* HighlightDataService items */}
            <div className="space-y-2">
              <Label>{t('platform.pages.test_pages.highlightdataservice_items_built_in')}</Label>
              <div className="max-h-40 overflow-y-auto space-y-2">
                {highlightDataService.getRegisteredHighlightItems().map((item) => (
                  <div
                    key={item.id}
                    className="flex items-center justify-between p-2 bg-gray-50 border rounded"
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700">
                          {item.type}
                        </Badge>
                        <span className="text-sm text-gray-600">{item.keys.join(', ')}</span>
                      </div>
                    </div>
                    <span className="text-xs text-gray-500">{t('platform.pages.test_pages.built_in')}</span>
                  </div>
                ))}
              </div>
            </div>

            <Button
              variant="outline"
              onClick={clearAllHighlights}
              className="w-full"
            >
              Clear All Highlights
            </Button>

            {/* Test highlighting button */}
            <Button
              variant="outline"
              onClick={() => {
                console.log('TestPage - Testing highlighting with items:', highlightItems);
                console.log('TestPage - Testing with content:', testContent);
                // Force a re-render of the TextBlock
                setTestContent(testContent + ' ');
              }}
              className="w-full bg-green-50 border-green-200 text-green-700 hover:bg-green-100"
            >
              Test Highlighting
            </Button>
          </CardContent>
        </Card>

        {/* Text Editor */}
        <Card>
          <CardHeader>
            <CardTitle>{t('platform.pages.test_pages.text_editor_with_highlighting')}</CardTitle>
            <CardDescription>
              Type or paste text to see automatic highlighting. Select text to manually apply highlights.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <TextBlock
              id="test-editor"
              content={testContent}
              placeholder={t('platform.pages.test_pages.type_or_paste_text_here_to_see_highlighting_in_action')}
              isActive={true}
              onContentChange={handleContentChange}
              onEnter={handleEnter}
              onBackspace={handleBackspace}
              onFocus={handleFocus}
              onArrowUp={handleArrowUp}
              onArrowDown={handleArrowDown}
              onCommand={handleCommand}
              enableAutoHighlighting={true}
            />
          </CardContent>
        </Card>
      </div>

      {/* Instructions */}
      <Card>
        <CardHeader>
          <CardTitle>{t('platform.pages.test_pages.how_to_use_highlighting')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <h4 className="font-semibold mb-2">{t('platform.pages.test_pages.automatic_highlighting')}</h4>
              <ul className="text-sm text-gray-600 space-y-1">
                <li>• Text is automatically highlighted as you type</li>
                <li>• Matches are found based on highlight keys</li>
                <li>• Different types have different visual styles</li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-2">{t('platform.pages.test_pages.manual_highlighting')}</h4>
              <ul className="text-sm text-gray-600 space-y-1">
                <li>• Select text to see the bubble menu</li>
                <li>• Click the sparkles icon to open highlight options</li>
                <li>• Choose from available highlight types</li>
                <li>• Remove highlights with the "Remove Highlight" option</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default TextHighlightingTestPage;
